import React, { memo, useCallback, useEffect, useMemo, useRef, useState } from "react"
import { Box } from "@material-ui/core"
import theme from "../../config/theme"

const themePalette = theme.palette
const webkitScrollbar = theme.scrollbars.webkitScrollbar
const webkitscrollbarTrack = theme.scrollbars.webkitScrollbarTrack

const DEFAULT_LIST_PADDING = 12
const DEFAULT_ROW_GAP = 8
const DEFAULT_OVERSCAN_PX = 420
const DEFAULT_CONTAINER_STYLE = {
  flex: 1,
  minHeight: 0,
}
const DEFAULT_CONTENT_STYLE = { margin: 0 }

const getDefaultNodeId = (node) => node?.id
const getDefaultNodeChildren = (node) => node?.children || node?.hijos || []

// Recorre un arbol visible y devuelve solo las ramas abiertas con la metadata necesaria para dibujarlo.
export const flattenVisibleTree = ({
  tree,
  expandedIds,
  getNodeId = getDefaultNodeId,
  getChildren = getDefaultNodeChildren,
}) => {
  const flattenedNodes = []

  const walkTree = (nodes, depth = 0, ancestorHasNext = []) => {
    const safeNodes = nodes || []

    safeNodes.forEach((node, index) => {
      const nodeId = getNodeId(node)
      const children = getChildren(node) || []
      const hasChildren = children.length > 0
      const hasNextSibling = index < safeNodes.length - 1
      const isExpanded = hasChildren && expandedIds.has(nodeId)

      flattenedNodes.push({
        id: nodeId,
        node,
        depth,
        hasChildren,
        isExpanded,
        hasNextSibling,
        ancestorHasNext,
      })

      if (hasChildren && isExpanded) {
        walkTree(children, depth + 1, [...ancestorHasNext, hasNextSibling])
      }
    })
  }

  walkTree(tree)

  return flattenedNodes
}

// Reune ids expandibles para inicializar o sincronizar arboles reutilizables.
export const collectExpandableNodeIds = (
  tree,
  getNodeId = getDefaultNodeId,
  getChildren = getDefaultNodeChildren
) => {
  const expandableIds = []

  const walkTree = (nodes) => {
    ;(nodes || []).forEach((node) => {
      const children = getChildren(node) || []
      if (children.length > 0) {
        expandableIds.push(getNodeId(node))
        walkTree(children)
      }
    })
  }

  walkTree(tree)

  return expandableIds
}

const findStartIndex = (offsets, heights, targetOffset) => {
  let low = 0
  let high = offsets.length - 1
  let result = offsets.length

  while (low <= high) {
    const middle = Math.floor((low + high) / 2)
    const itemEnd = offsets[middle] + heights[middle]

    if (itemEnd >= targetOffset) {
      result = middle
      high = middle - 1
    } else {
      low = middle + 1
    }
  }

  return result
}

function VirtualizedTreeList({
  tree,
  expandedIds,
  getNodeId = getDefaultNodeId,
  getChildren = getDefaultNodeChildren,
  renderRow,
  getRowHeight,
  virtualization,
  layout,
  emptyState,
  scrollRef,
  hidden = false,
}) {
  const {
    listPadding = DEFAULT_LIST_PADDING,
    listPaddingTop = listPadding,
    listPaddingBottom = listPadding,
    listPaddingLeft = listPadding,
    listPaddingRight = listPadding,
    rowGap = DEFAULT_ROW_GAP,
    overscanPx = DEFAULT_OVERSCAN_PX,
  } = virtualization || {}
  const {
    containerStyle = DEFAULT_CONTAINER_STYLE,
    contentStyle = DEFAULT_CONTENT_STYLE,
  } = layout || {}
  const listScrollRef = useRef(null)
  const [scrollTop, setScrollTop] = useState(0)
  const [viewportHeight, setViewportHeight] = useState(0)

  const setListRef = useCallback((node) => {
    listScrollRef.current = node

    if (scrollRef && typeof scrollRef === "object") {
      scrollRef.current = node
    }

    if (node) {
      setViewportHeight(node.clientHeight)
      setScrollTop(node.scrollTop)
    }
  }, [scrollRef])

  const visibleTreeNodes = useMemo(
    () => flattenVisibleTree({ tree, expandedIds, getNodeId, getChildren }),
    [expandedIds, getChildren, getNodeId, tree]
  )

  useEffect(() => {
    const node = listScrollRef.current
    if (!node) return undefined

    const updateViewportHeight = () => {
      setViewportHeight(node.clientHeight)
    }

    updateViewportHeight()

    if (typeof ResizeObserver === "function") {
      const observer = new ResizeObserver(updateViewportHeight)
      observer.observe(node)

      return () => observer.disconnect()
    }

    window.addEventListener("resize", updateViewportHeight)
    return () => window.removeEventListener("resize", updateViewportHeight)
  }, [])

  useEffect(() => {
    if (hidden) return

    const node = listScrollRef.current
    if (!node) return

    setViewportHeight(node.clientHeight)
    setScrollTop(node.scrollTop)
  }, [hidden])

  const virtualizedMetrics = useMemo(() => {
    const heights = []
    const offsets = []
    let accumulatedHeight = listPaddingTop

    visibleTreeNodes.forEach((treeItem) => {
      offsets.push(accumulatedHeight)
      const rowHeight = getRowHeight(treeItem) + rowGap
      heights.push(rowHeight)
      accumulatedHeight += rowHeight
    })

    return {
      heights,
      offsets,
      totalHeight: accumulatedHeight + listPaddingBottom,
    }
  }, [getRowHeight, listPaddingBottom, listPaddingTop, rowGap, visibleTreeNodes])

  const { visibleRows, totalHeight } = useMemo(() => {
    if (visibleTreeNodes.length === 0) {
      return { visibleRows: [], totalHeight: listPaddingTop + listPaddingBottom }
    }

    const { heights, offsets, totalHeight: nextTotalHeight } = virtualizedMetrics
    const viewportTop = Math.max(0, scrollTop - overscanPx)
    const viewportBottom = scrollTop + Math.max(viewportHeight, 0) + overscanPx
    const startIndex = Math.max(0, findStartIndex(offsets, heights, viewportTop) - 1)
    let endIndex = startIndex

    while (endIndex < visibleTreeNodes.length && offsets[endIndex] < viewportBottom) {
      endIndex += 1
    }

    const nextVisibleRows = []

    for (let index = startIndex; index < Math.min(endIndex + 1, visibleTreeNodes.length); index += 1) {
      nextVisibleRows.push({
        item: visibleTreeNodes[index],
        top: offsets[index],
      })
    }

    return {
      visibleRows: nextVisibleRows,
      totalHeight: nextTotalHeight,
    }
  }, [listPaddingBottom, listPaddingTop, overscanPx, scrollTop, viewportHeight, virtualizedMetrics, visibleTreeNodes])

  const handleScroll = useCallback((event) => {
    setScrollTop(event.currentTarget.scrollTop)
  }, [])

  return (
    <Box
      ref={setListRef}
      onScroll={handleScroll}
      style={{
        overflowY: "scroll",
        overflowX: "hidden",
        minHeight: 0,
        flex: 1,
        display: hidden ? "none" : "block",
        ...containerStyle,
      }}
      sx={{
        "&::-webkit-scrollbar": {
          ...webkitScrollbar,
        },
        "&::-webkit-scrollbar-track": {
          ...webkitscrollbarTrack,
        },
        "&::-webkit-scrollbar-thumb": {
          background: themePalette.primary.main,
        },
      }}
    >
      {visibleTreeNodes.length === 0 ? (
        emptyState || null
      ) : (
        <Box
          style={{
            position: "relative",
            minHeight: `${Math.max(totalHeight, viewportHeight || 0)}px`,
            ...contentStyle,
          }}
        >
          {visibleRows.map(({ item, top }) => (
            <Box
              key={item.id}
              style={{
                position: "absolute",
                top: `${top}px`,
                left: `${listPaddingLeft}px`,
                right: `${listPaddingRight}px`,
                paddingBottom: `${rowGap}px`,
                boxSizing: "border-box",
              }}
            >
              {renderRow(item)}
            </Box>
          ))}
        </Box>
      )}
    </Box>
  )
}

export default memo(VirtualizedTreeList)
